/**
 * Target-stock (order-up-to) reorder math for the Buying Table replenishment
 * dialog — US-102.
 *
 * Extracted from BuyingTable.vue so the formula can be unit-tested without
 * instantiating the full Vue component (mirrors buyingTableSortColumns.ts).
 *
 * This mirrors the assisty-bot backend reorder formula so the dialog's
 * Order Calculation breakdown reconciles to the backend's suggested quantity
 * ([inventory_replenishment]).
 *
 *   S (target stock)  = leadTimeDemand + coverageDemand + safetyStock
 *                       // demand over the protection interval (L + R) + safety stock
 *   Position          = onHand + incomingBeforeLeadTime + incomingAfterLeadTime
 *                       // on-hand is ALWAYS raw — negative inventory (oversold / backorders) is
 *                       // counted directly, so the position itself pulls the order up to clear
 *                       // oversold units. No on-hand floor, no preorders branch.
 *                       // When suggestFuture is set (buying calendar / "show all"), the position is
 *                       // projected to the reorder date: LEAST(Position, leadTimeDemand + safetyStock
 *                       // + incomingAfter) — see US-102 Task 8.
 *   lostSalesAdj      = continueSelling ? 0 : max(0, leadTimeDemand − max(0, onHand + incomingBefore))
 *                       // 'deny' variants: lead-time demand that can't be served from sellable
 *                       // stock before arrival is lost, not restocked
 *   reorderPoint      = leadTimeDemand + safetyStock
 *   triggered         = suggestFuture || (onHand + incomingBefore) <= reorderPoint
 *   base              = triggered ? max(0, S − Position − lostSalesAdj) : 0
 *                       // (s, S) continuous review: order up to S, but ONLY when due. Order-now
 *                       // ('no') gates at the reorder point — 0 above ROP; look-ahead ('yes') bypasses
 *                       // the gate and projects (buying calendar). See US-102 (s, S), 2026-06-10.
 *
 * Backorder handling (US-102 update 2026-06-09): there is NO separate "oversold add-back" term.
 * Negative on-hand already lowers the Inventory Position, which is exactly what pulls the order up
 * to cover the oversold units — adding them again would double-count (the lead-time demand inside S
 * already covers them). This applies to both 'continue' and 'deny' variants.
 *
 * Reorder Point / Reorder Date are a separate timing layer and are unchanged.
 */

export interface IReplenishmentMathInput {
  onHand: number;
  incomingBeforeLeadTime: number;
  incomingAfterLeadTime: number;
  leadTimeDemand: number;        // forecast demand over lead time (D_L)
  coverageDemand: number;        // forecast demand over reorder cycle (D_R)
  safetyStock: number;
  continueSelling: boolean;      // Shopify "continue selling when out of stock"
  // Buying-calendar / "show all" mode: project the Inventory Position to the future reorder date
  // (mirrors the backend's suggest_future_reorder_quantity='yes' path). Default false = order-now.
  suggestFuture?: boolean;
  // Effective per-variant Maximum Inventory ([variant_max_inventory_quantity] — already
  // scope-proportioned by the backend). 0/absent = no cap configured.
  variantMaxInventory?: number;
  fixedOrderQuantity?: number;
}

// Why the BASE suggestion is zero — drives the timeline explanation.
//  'not_due'  order-now gate: stock + incoming-before is above the reorder point
//  'covered'  due, but stock + incoming already meets the target (nothing to top up)
// (A positive base fully cancelled by the Maximum-Inventory cap is reported via
//  isCappedByMaxInventory + cappedSuggestedQuantity, not as a zero reason.)
export type ReplenishmentZeroReason = 'not_due' | 'covered' | null;

export interface IReplenishmentMathResult {
  targetStockLevel: number;        // S
  reorderPoint: number;            // D_L + SS (the (s,S) trigger)
  inventoryPosition: number;       // onHand (raw, negative included) + incoming
  lostSalesAdjustment: number;     // 0 for continue
  baseSuggestedQuantity: number;   // periodic: max(0, S − position − adjustment); fixed lot: max(0, fixed − I_after); 0 when not due
  rawSuggestedQuantity: number;    // === baseSuggestedQuantity (pre cap/rounding)
  // Max-Inventory cap mirror (backend: LEAST(base, max(0, variant_max − I_after − arrival surplus))):
  projectedArrivalStock: number;   // max(0, onHand + incomingBefore − leadTimeDemand) — surplus at arrival
  maxInventoryCapRoom: number | null; // room left under variant max at arrival; null = no cap configured
  cappedSuggestedQuantity: number; // min(base, capRoom) — what the backend's cap allows
  isCappedByMaxInventory: boolean; // base exceeds the cap room (fully or partially reduced)
  zeroReason: ReplenishmentZeroReason;
  isFixedOrder: boolean;
}

const num = (v: number | undefined | null): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function computeReplenishmentMath(input: IReplenishmentMathInput): IReplenishmentMathResult {
  const onHand = num(input.onHand);
  const incomingBeforeLeadTime = num(input.incomingBeforeLeadTime);
  const incomingAfterLeadTime = num(input.incomingAfterLeadTime);
  const leadTimeDemand = num(input.leadTimeDemand);
  const coverageDemand = num(input.coverageDemand);
  const safetyStock = num(input.safetyStock);
  const continueSelling = !!input.continueSelling;
  const suggestFuture = !!input.suggestFuture;
  const variantMaxInventory = num(input.variantMaxInventory);
  const fixedOrderQuantity = num(input.fixedOrderQuantity);

  // S = demand over protection interval (lead time + reorder cycle) + safety stock
  const targetStockLevel = leadTimeDemand + coverageDemand + safetyStock;

  // Inventory Position counts on-hand raw (negative backorders included) + all incoming.
  const reorderPoint = leadTimeDemand + safetyStock;
  const todayPosition = onHand + incomingBeforeLeadTime + incomingAfterLeadTime;
  // (s, S) continuous review: order up to S, but ONLY when the product is due.
  //  • order-now ('no') gates at the reorder point — 0 above ROP (no middle-zone nervousness,
  //    no daily re-suggestion after a PO, which lifts the position back above ROP).
  //  • look-ahead ('yes', buying calendar / "show all") bypasses the gate and projects the Position
  //    to the reorder date (≈ reorder_point + incoming-after; LEAST keeps today's lower position for
  //    already-overdue catch-up). Mirrors the backend US-102 (s, S) gate.
  const triggered = suggestFuture || (onHand + incomingBeforeLeadTime) <= reorderPoint;
  const inventoryPosition = suggestFuture
    ? Math.min(todayPosition, reorderPoint + incomingAfterLeadTime)
    : todayPosition;

  // Lost-sales adjustment ('deny' only): lead-time demand that can't be served from sellable
  // stock (on-hand + incoming-before, floored at zero) before the new order arrives.
  const lostSalesAdjustment = continueSelling
    ? 0
    : Math.max(0, leadTimeDemand - Math.max(0, onHand + incomingBeforeLeadTime));

  // Fixed-order-quantity products keep (s,Q) semantics: the fixed lot (minus incoming-after) when
  // triggered. Computing their base here (instead of the meaningless periodic value) makes the cap
  // mirror below branch-correct — the backend's max-inventory cap wraps the fixed lot too.
  const baseSuggestedQuantity = !triggered
    ? 0
    : fixedOrderQuantity > 0
      ? Math.max(0, fixedOrderQuantity - incomingAfterLeadTime)
      : Math.max(0, targetStockLevel - inventoryPosition - lostSalesAdjustment);

  // Max-Inventory cap mirror. Backend: the order is capped so that projected stock at arrival
  // + incoming-after + the order doesn't exceed the variant's effective Maximum Inventory.
  // Under suggestFuture the order is placed at the FUTURE reorder date, so sellable stock is
  // projected to ≤ reorderPoint first (same projection as the position) — otherwise healthy
  // far-future products would be zeroed by today's surplus. nullif(max, 0) → 0/absent = no cap.
  const sellableForCap = suggestFuture
    ? Math.min(onHand + incomingBeforeLeadTime, reorderPoint)
    : onHand + incomingBeforeLeadTime;
  const projectedArrivalStock = Math.max(0, sellableForCap - leadTimeDemand);
  const maxInventoryCapRoom = variantMaxInventory > 0
    ? Math.max(0, variantMaxInventory - incomingAfterLeadTime - projectedArrivalStock)
    : null;
  const cappedSuggestedQuantity = maxInventoryCapRoom === null
    ? baseSuggestedQuantity
    : Math.min(baseSuggestedQuantity, maxInventoryCapRoom);
  const isCappedByMaxInventory = maxInventoryCapRoom !== null
    && baseSuggestedQuantity > maxInventoryCapRoom;

  let zeroReason: ReplenishmentZeroReason = null;
  if (!triggered) {
    zeroReason = 'not_due';
  } else if (baseSuggestedQuantity <= 0) {
    zeroReason = 'covered';
  }

  return {
    targetStockLevel,
    reorderPoint,
    inventoryPosition,
    lostSalesAdjustment,
    baseSuggestedQuantity,
    rawSuggestedQuantity: baseSuggestedQuantity,
    projectedArrivalStock,
    maxInventoryCapRoom,
    cappedSuggestedQuantity,
    isCappedByMaxInventory,
    zeroReason,
    isFixedOrder: fixedOrderQuantity > 0,
  };
}
